import { useEffect, useState } from 'react';
import DateField from '../components/DateField.jsx';
import QuickPeriod from '../components/QuickPeriod.jsx';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  api, withQuery, money, date,
  DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS,
} from '../api.js';
import { Pager } from './Products.jsx';

const PAGE = 50;

/*
 * Филтрите и страницата живеят в адреса, не в локално състояние.
 *
 * Иначе бутонът „Назад" на браузера връща на празен списък с нулирани
 * филтри: адресът се пази в историята, но React state — не. С адреса
 * като единствен източник на истина „Назад" връща точно списъка,
 * какъвто е бил — филтри и страница — по себе си.
 */
function citiFiltri(params) {
  return {
    type: params.get('type') ?? '',
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    q: params.get('q') ?? '',
    partnerQ: params.get('partnerQ') ?? '',
    unpaidOnly: params.get('unpaidOnly') === '1',
  };
}

export default function Documents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const filters = citiFiltri(searchParams);
  const offset = Number(searchParams.get('offset') ?? 0);

  const [data, setData] = useState({ items: [], total: 0, sumBase: '0.00' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Автофокусът в търсенето пречи при връщане назад — открадва скрола.
  const [hasSavedScroll] = useState(
    () => sessionStorage.getItem(`documents-scroll:${location.key}`) != null
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api.get(withQuery('/api/documents', { ...filters, limit: PAGE, offset }))
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, (filters.q || filters.partnerQ) ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  /* Пази позицията на скрола за този запис в историята, преди да го напуснем. */
  useEffect(() => {
    const key = `documents-scroll:${location.key}`;
    return () => sessionStorage.setItem(key, String(window.scrollY));
  }, [location.key]);

  /* Връща я веднъж, след като редовете са налице — не при всяко презареждане. */
  useEffect(() => {
    if (loading) return;
    const key = `documents-scroll:${location.key}`;
    const saved = sessionStorage.getItem(key);
    if (saved == null) return;
    sessionStorage.removeItem(key);
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, Number(saved))));
  }, [loading, location.key]);

  function change(patch) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === '' || v === false || v == null) next.delete(k);
        else next.set(k, v === true ? '1' : String(v));
      }
      next.delete('offset');
      return next;
    }, { replace: true });
  }

  function changeOffset(next) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set('offset', String(next)); else p.delete('offset');
      return p;
    }, { replace: true });
  }

  return (
    <>
      <div className="page-head">
        <h1>Документи</h1>
        <div className="spacer" />
        <span className="muted">общо {money(data.sumBase)}</span>
      </div>

      <div className="tabs">
        <button
          className={`tab ${filters.type === 'sale' ? 'active' : ''}`}
          onClick={() => change({ type: 'sale' })}
        >
          Продажби
        </button>
        <button
          className={`tab ${filters.type === 'purchase' ? 'active' : ''}`}
          onClick={() => change({ type: 'purchase' })}
        >
          Доставки
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          Контрагент
          <input
            type="search"
            autoFocus={!hasSavedScroll}
            autoComplete="off"
            placeholder="име, ЕИК, ДДС номер или телефон"
            value={filters.partnerQ}
            onChange={(e) => change({ partnerQ: e.target.value })}
          />
        </label>
        <label className="field">
          Търсене
          <input
            type="search"
            autoComplete="off"
            placeholder="номер, фактура или УНП"
            value={filters.q}
            onChange={(e) => change({ q: e.target.value })}
          />
        </label>
        <label className="field">
          Вид
          <select value={filters.type} onChange={(e) => change({ type: e.target.value })}>
            <option value="">всички</option>
            {Object.entries(DOC_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="field">
          Състояние
          <select value={filters.status} onChange={(e) => change({ status: e.target.value })}>
            <option value="">всички</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="field">
          От дата
          <DateField value={filters.from} onChange={(v) => change({ from: v })} />
        </label>
        <label className="field">
          До дата
          <DateField value={filters.to} onChange={(v) => change({ to: v })} />
        </label>
        <div className="field">
          <span aria-hidden="true" style={{ visibility: 'hidden' }}>·</span>
          <QuickPeriod onPick={({ from, to }) => change({ from, to })} />
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input
            type="checkbox"
            checked={filters.unpaidOnly}
            onChange={(e) => change({ unpaidOnly: e.target.checked })}
          />
          само неплатени
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Контрагент</th>
                <th>Дата</th>
                <th>Фактура</th>
                <th>Документ</th>
                <th>Склад</th>
                <th className="num">Сума</th>
                <th className="num">Платено</th>
                <th>Състояние</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>
                      {d.partner_name
                        ? <Link to={`/documents/${d.id}`}>{d.partner_name}</Link>
                        : <span className="muted">на каса</span>}
                    </strong>
                  </td>
                  <td>{date(d.doc_date)}</td>
                  <td className="muted">{d.invoice_no ?? '—'}</td>
                  <td><Link to={`/documents/${d.id}`}>{d.doc_number}</Link></td>
                  <td className="muted">{d.warehouse_name ?? '—'}</td>
                  <td className="num">{money(d.total, d.currency)}</td>
                  <td className="num">
                    {Number(d.paid_amount) >= Number(d.total)
                      ? <span className="badge ok">платено</span>
                      : money(d.paid_amount, d.currency)}
                  </td>
                  <td><span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span></td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="empty">Няма намерени документи</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager total={data.total} offset={offset} setOffset={changeOffset} loading={loading} page={PAGE} />
      </div>
    </>
  );
}
